import React, { Component, Fragment } from 'react';
import { Placement } from '@philips-internal/oswald-ui-constants';
import { Container } from '@philips-internal/oswald-ui-core';
import { ClickEvent, CloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, generateUUID, isEmptyArrayOrObject, isFunction, trigger } from '@philips-internal/oswald-ui-utils';
import { ActionStyle } from '../../../action/action-constants.js';
import { Button } from '../../../button/button.js';
import { Checkbox } from '../../../checkbox/checkbox.js';
import { CollapseArrow } from '../../../collapse-arrow/collapse-arrow.js';
import { InlineImage } from '../../../inline-image/inline-image.js';
import { Option } from '../../../option/option.js';
import { OptionGroup } from '../../../option-group/option-group.js';
import { Popover } from '../../../popover/popover.js';
import { getEnabledOptions } from '../heading-context/heading-context-utils.js';
import { HeadingActionButton } from './heading-action-button.js';
import './heading-action.css';
const HeadingActionContainer = {
    name: '',
};
class HeadingAction extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            open: false,
        };
        this.onClick = () => {
            this.props.action.onClick(new ClickEvent());
        };
        this.onDoubleClick = () => {
            if (isFunction(this.props.action.onDoubleClick)) {
                this.props.action.onDoubleClick(new ClickEvent());
            }
        };
        this.onContextMenuClick = () => {
            if (isFunction(this.props.action.onContextMenuClick)) {
                this.props.action.onContextMenuClick(new ClickEvent());
            }
        };
        this.onClose = () => {
            const { onClose } = this.props;
            this.setState({ open: false }, trigger(onClose, new CloseEvent()));
        };
        this.onCheckboxChange = (event) => {
            const { action } = this.props;
            const { onChange } = action;
            this.setState({ selected: event.value }, () => {
                this.onClick();
                if (!isFunction(onChange)) {
                    return;
                }
                onChange(event);
            });
        };
    }
    getOptions() {
        const { action } = this.props;
        const { options } = action;
        return getEnabledOptions(options);
    }
    getHeadingActionsFromOptions() {
        return this.getOptions()
            .map(option => (React.createElement(HeadingAction, { action: option, key: option.id, onClose: this.onClose })));
    }
    getClassName(mainClass) {
        const { action } = this.props;
        const { className } = action;
        return classNames({
            [mainClass]: true,
            [`${className}`]: Boolean(className),
        });
    }
    renderTextButton() {
        const { action } = this.props;
        const { buttonTheme, icon, iconPlacement, text } = action;
        return (React.createElement(Button, { className: this.getClassName('c-heading-action__button-text'), icon: icon, iconPlacement: iconPlacement, onClick: this.onClick, onContextMenuClick: this.onContextMenuClick, onDoubleClick: this.onDoubleClick, theme: buttonTheme, value: text }));
    }
    renderIconButton() {
        const { action } = this.props;
        return (React.createElement(HeadingActionButton, { action: action, buttonClassName: this.getClassName('c-heading-action__button-icon'), inlineImageClassName: this.getClassName('c-heading-action__icon-dark'), onClick: this.onClick, onContextMenuClick: this.onContextMenuClick, onDoubleClick: this.onDoubleClick }));
    }
    renderText() {
        const { action } = this.props;
        const { iconPlacement, text = '' } = action;
        const baseClassName = this.getClassName('c-heading-action__action-text u-hide@empty');
        const className = classNames({
            [baseClassName]: true,
            'c-heading-action__action-text--icon-right': iconPlacement === Placement.RIGHT,
        });
        return (React.createElement("div", { className: className, onClick: this.onClick },
            this.renderInlineImage(true),
            MessageService.translate(text)));
    }
    renderOptionsWithContainer() {
        const options = this.getHeadingActionsFromOptions();
        return (React.createElement(Fragment, null,
            React.createElement(Container, { uuid: this.props.action.id }),
            options));
    }
    renderCheckbox() {
        const { action } = this.props;
        const { selected } = this.state;
        const uuid = generateUUID();
        return (React.createElement(Checkbox, { className: "c-heading-action__checkbox", identifier: uuid, label: action.text, name: "heading-action-checkbox", onChange: this.onCheckboxChange, uuid: `heading-action-checkbox-${uuid}`, value: selected }));
    }
    renderDropdown() {
        const { action } = this.props;
        const { open } = this.state;
        return (React.createElement(Popover, { onClose: this.onClose, open: open },
            React.createElement("div", null,
                React.createElement(Button, { icon: React.createElement(CollapseArrow, { className: "c-heading-action__action-dropdown-icon", collapsed: !open }), iconPlacement: Placement.RIGHT, onClick: () => this.setState({ open: true }, this.onClick), onContextMenuClick: this.onContextMenuClick, onDoubleClick: this.onDoubleClick, theme: action.buttonTheme, value: action.text })),
            React.createElement(Popover.Content, null,
                React.createElement(Fragment, null, this.renderOptionsWithContainer()))));
    }
    renderOption() {
        const { action } = this.props;
        const option = (React.createElement(Option, { enabled: true, hasSuboptions: !isEmptyArrayOrObject(this.getOptions()), onClick: this.onClick, onClose: this.onClose, onContextMenuClick: action.onContextMenuClick, preventDefault: false, stopPropagation: false, uuid: action.id, value: action.text }, this.renderOptionsWithContainer()));
        return (React.createElement(Container, { uuid: action.id }, option));
    }
    renderOptionGroup() {
        const { action } = this.props;
        return (React.createElement(OptionGroup, { title: action.text, uuid: action.id }, this.getHeadingActionsFromOptions()));
    }
    renderInlineImage(preventClick = false) {
        const { action } = this.props;
        const { icon } = action;
        const onClick = () => {
            if (preventClick) {
                return;
            }
            this.onClick();
        };
        return icon
            ? React.createElement(InlineImage, { className: this.getClassName('c-heading-action__icon-dark'), onClick: onClick, src: icon })
            : null;
    }
    render() {
        const { action } = this.props;
        const { style } = action;
        const renderers = {
            [ActionStyle.BUTTON]: () => this.renderTextButton(),
            [ActionStyle.CHECKBOX]: () => this.renderCheckbox(),
            [ActionStyle.DROPDOWN]: () => this.renderDropdown(),
            [ActionStyle.ICON]: () => this.renderIconButton(),
            [ActionStyle.INLINE_IMAGE]: () => this.renderInlineImage(),
            [ActionStyle.OPTION]: () => this.renderOption(),
            [ActionStyle.OPTION_GROUP]: () => this.renderOptionGroup(),
            [ActionStyle.TEXT]: () => this.renderText(),
        };
        return renderers[style]();
    }
}
export { HeadingAction, HeadingActionContainer, };
//# sourceMappingURL=heading-action.js.map